// Shared types for ECHOLOOP.

export const TILE = 30;
export const VIEW_W = 960;
export const VIEW_H = 540;
export const TICK = 1 / 60; // fixed sim step; recordings are arrays of per-tick frames
export const MAX_TAPE = 60 * 120; // 2-minute cap per attempt recording

/** One recorded frame of the player's run. */
export interface Frame {
  x: number;
  y: number;
  face: 1 | -1;
  grounded: boolean;
}

/** A spawned echo replaying a finished tape. Freezes on its last frame. */
export interface Echo {
  tape: Frame[];
  hue: number; // accent hue for rendering
  id: number; // 1-based order of creation
}

export interface ButtonDef {
  id: number; // digit from the grid
  col: number;
  row: number;
}

/** A floor lever (grid 'a'-'d'). Stepping on it FLIPS its door open<->shut and
 *  the new state STICKS — no echo needed to hold it, unlike a button. */
export interface LeverDef {
  letter: string; // the door it flips (uppercase of the grid char)
  col: number;
  row: number;
}

export type DoorMode = 'hold' | 'toggle' | 'timed';

export interface DoorDef {
  letter: string;
  cells: Array<{ col: number; row: number }>;
  needs: number[]; // hold/timed mode: ALL of these button ids must be pressed
  mode: DoorMode; // 'toggle' = lever-driven; 'timed' = momentary seal (see windowTicks)
  /** 'timed' mode only: ticks the gate stays open after a FRESH (rising-edge)
   *  press, then auto-shuts even if the pad is still held. A frozen echo is a
   *  steady press → produces no rising edge → can never re-arm a timed seal. */
  windowTicks?: number;
}

export interface LevelDef {
  name: string;
  subtitle: string;
  hint: string;
  echoes: number; // echo budget
  accent: string; // level accent color
  accent2: string; // secondary accent
  grid: string[]; // ASCII rows — see levels.ts legend
  doors: Record<string, number[]>;
  /** door letters that are momentary TIMED seals → window length in ticks. A
   *  rising-edge press opens that gate for `windowTicks`, then it auto-shuts.
   *  Such a door must ALSO carry a button rule in `doors`. */
  timed?: Record<string, number>;
  /** the door letter SEALING the exit: a win only counts while this (timed)
   *  door is open. Omit ⇒ the exit is always armed (every legacy level). */
  seal?: string;
}

export interface ParsedLevel {
  def: LevelDef;
  cols: number;
  rows: number;
  /**
   * tile lookup after spawn/button/lever cells are stripped to '.':
   *   '#' solid · '-' one-way · '^' spikes · '*' spring pad · 'x' crumble slab
   *   'A'-'D' door cells · '.' empty
   */
  tiles: string[][];
  spawn: { x: number; y: number };
  exit: { col: number; row: number };
  buttons: ButtonDef[];
  levers: LeverDef[];
  doors: DoorDef[];
  /** cells holding a crumble slab ('x'), gathered once for the sim's bookkeeping */
  crumbles: Array<{ col: number; row: number }>;
}

export type FxKind =
  | 'echo'
  | 'death'
  | 'door'
  | 'land'
  | 'win'
  | 'deny'
  | 'spring'
  | 'lever'
  | 'crumble';

export interface FxEvent {
  kind: FxKind;
  x: number;
  y: number;
}
